import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ClipboardCheck,
  ArrowRight,
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle2,
  Sparkles,
  PartyPopper,
  Clock,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useMyPendingSurveys, useRespondSurvey } from '@/hooks/useSurveys';
import { usePublicGamificationSettings } from '@/hooks/useGamification';
import { useAuthStore } from '@/stores/auth';
import type { Survey, SurveyQuestion } from '@/services/surveys.service';
import {
  Dialog,
  DialogContent,
  Button,
  Input,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@xtreed/academy-shared/ui';

// Event bus for programmatic triggers
type SurveyTriggerCallback = (ctx: {
  type: string;
  lessonId?: string;
  moduleId?: string;
  productId?: string;
}) => void;
const listeners: SurveyTriggerCallback[] = [];
export function onSurveyTrigger(cb: SurveyTriggerCallback) {
  listeners.push(cb);
  return () => {
    const i = listeners.indexOf(cb);
    if (i >= 0) listeners.splice(i, 1);
  };
}
export function triggerSurvey(ctx: {
  type: string;
  lessonId?: string;
  moduleId?: string;
  productId?: string;
}) {
  listeners.forEach((cb) => cb(ctx));
}

export function SurveyModal() {
  const { t } = useTranslation('survey');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: pending = [], refetch } = useMyPendingSurveys();
  const respondMutation = useRespondSurvey();

  const [activeSurvey, setActiveSurvey] = useState<Survey | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1..n = questions, n+1 = submitting/done
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<'intro' | 'questions' | 'done'>('intro');

  // Auto-show pending survey
  useEffect(() => {
    if (!isAuthenticated || activeSurvey || phase === 'done') return;
    const next = pending.find((s) => !dismissed.has(s.id));
    if (next) {
      const t = setTimeout(() => {
        setActiveSurvey(next);
        setCurrentStep(0);
        setPhase('intro');
        setAnswers({});
      }, 1000);
      return () => clearTimeout(t);
    }
  }, [pending, isAuthenticated, dismissed, activeSurvey, phase]);

  useEffect(() => {
    return onSurveyTrigger(() => refetch());
  }, [refetch]);

  const questions = useMemo(
    () => activeSurvey?.questions || [],
    [activeSurvey],
  );
  const totalSteps = questions.length;
  const { data: gamificationSettings } = usePublicGamificationSettings();
  const xpReward = gamificationSettings?.enabled
    ? (activeSurvey?.xpReward ?? 10)
    : 0;
  const currentQuestion =
    phase === 'questions' ? questions[currentStep - 1] : null;
  const progress =
    phase === 'intro'
      ? 0
      : phase === 'done'
        ? 100
        : totalSteps === 0
          ? 0
          : Math.round(((currentStep - 1) / totalSteps) * 100);

  const handleDismiss = useCallback(() => {
    if (activeSurvey) {
      setDismissed((prev) => new Set(prev).add(activeSurvey.id));
    }
    setActiveSurvey(null);
    setPhase('intro');
  }, [activeSurvey]);

  const handleNext = useCallback(() => {
    if (phase === 'intro') {
      setPhase('questions');
      setCurrentStep(1);
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep((s) => s + 1);
    }
  }, [phase, currentStep, totalSteps]);

  const handlePrev = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
    } else {
      setPhase('intro');
      setCurrentStep(0);
    }
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    if (!activeSurvey) return;
    const answerList = Object.entries(answers)
      .filter(([, v]) => v)
      .map(([questionId, value]) => ({ questionId, value }));
    await respondMutation.mutateAsync({
      surveyId: activeSurvey.id,
      answers: answerList,
    });
    setPhase('done');
    setTimeout(() => {
      setActiveSurvey(null);
      setPhase('intro');
      setAnswers({});
    }, 3000);
  }, [activeSurvey, answers, respondMutation]);

  const isLastQuestion = currentStep === totalSteps && phase === 'questions';

  if (!activeSurvey) return null;

  return (
    <Dialog open={!!activeSurvey} onOpenChange={(v) => !v && handleDismiss()}>
      <DialogContent className="w-[95vw] max-w-lg gap-0 overflow-hidden p-0">
        {/* Progress bar — step + bar + percent */}
        {phase !== 'intro' && (
          <div className="relative space-y-1.5 border-b border-border/40 bg-background/80 pt-4 pr-14 pb-3 pl-5 backdrop-blur-xs">
            <div className="flex items-center justify-between text-[10px] font-bold tracking-wider uppercase">
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <ClipboardCheck className="h-3 w-3" />
                {phase === 'done'
                  ? t('progress.done')
                  : t('progress.step', {
                      current: currentStep,
                      total: totalSteps,
                    })}
              </span>
              <span className="text-sm text-primary-ink">{progress}%</span>
            </div>
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted ring-1 ring-border/40">
              <div
                className="relative h-full rounded-full bg-linear-to-r from-primary to-xtreed-green-light shadow-[0_0_12px_rgba(57,211,83,0.5)] transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              >
                <div className="shimmer absolute inset-0 opacity-60" />
              </div>
            </div>
          </div>
        )}

        {/* INTRO */}
        {phase === 'intro' && (
          <div className="relative overflow-hidden">
            {/* Decorative gradient background */}
            <div className="pointer-events-none absolute inset-0 bg-linear-to-br from-primary/10 via-primary/5 to-transparent" />
            <div className="pointer-events-none absolute top-0 right-0 left-0 h-px bg-linear-to-r from-primary/0 via-primary/40 to-primary/0" />

            <div className="relative space-y-5 p-6 text-center">
              {/* Animated icon */}
              <div className="relative mx-auto h-20 w-20">
                <div className="absolute inset-0 animate-pulse rounded-2xl bg-linear-to-br from-primary/30 to-primary/10 blur-xl" />
                <div className="relative flex h-full w-full items-center justify-center rounded-2xl bg-linear-to-br from-primary/20 to-primary/5 ring-1 ring-primary/30">
                  <ClipboardCheck className="h-10 w-10 text-primary-ink" />
                </div>
                {/* Floating sparkle */}
                <Sparkles className="absolute -top-1 -right-1 h-5 w-5 animate-pulse text-warning" />
              </div>

              {/* Surprise tag */}
              <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-linear-to-r from-primary/10 to-xtreed-green-light/10 px-3 py-1">
                <Sparkles className="h-3 w-3 text-primary-ink" />
                <span className="text-[10px] font-bold tracking-wider text-primary-ink uppercase">
                  {t('intro.tag')}
                </span>
              </div>

              <div className="space-y-2">
                <h2 className="text-xl leading-tight font-bold sm:text-2xl">
                  {activeSurvey.title}
                </h2>
                {activeSurvey.description ? (
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {activeSurvey.description}
                  </p>
                ) : (
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {t('intro.defaultDescription')}
                  </p>
                )}
              </div>

              {/* Info chips */}
              <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-secondary/60 px-3 py-1 font-medium">
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  {t('intro.questions', { count: totalSteps })}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-secondary/60 px-3 py-1 font-medium">
                  <Clock className="h-3.5 w-3.5" />
                  {t('intro.duration', {
                    minutes: Math.max(1, Math.ceil(totalSteps * 0.3)),
                  })}
                </span>
                {xpReward > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-warning/40 bg-linear-to-r from-warning/15 to-warning/15 px-3 py-1 font-bold text-warning-ink">
                    <Zap className="h-3.5 w-3.5 fill-warning" />+{xpReward} XP
                  </span>
                )}
              </div>

              {/* CTA */}
              <div className="flex flex-col gap-2 pt-1">
                <Button
                  onClick={handleNext}
                  className="group w-full gap-2 bg-linear-to-r from-primary to-xtreed-green-light text-primary-foreground shadow-lg transition-all hover:opacity-95 hover:shadow-xl hover:shadow-primary/30"
                  size="lg"
                >
                  {t('intro.start')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t('intro.later')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QUESTIONS (one at a time) */}
        {phase === 'questions' && currentQuestion && (
          <div
            key={currentQuestion.id}
            className="animate-[fadeSlideIn_0.35s_ease-out] p-6 pt-5"
          >
            {/* Step badge */}
            <div className="mb-4 flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary-ink ring-1 ring-primary/20">
                {currentStep}
              </span>
              <div className="h-px flex-1 bg-border/50" />
            </div>

            {/* Question */}
            <div className="min-h-[200px] space-y-5">
              <div className="space-y-1.5">
                <h3 className="text-lg leading-snug font-bold sm:text-xl">
                  {currentQuestion.label}
                  {currentQuestion.required && (
                    <span className="ml-1 text-destructive">*</span>
                  )}
                </h3>
                {currentQuestion.description && (
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {currentQuestion.description}
                  </p>
                )}
              </div>

              <QuestionInput
                question={currentQuestion}
                value={answers[currentQuestion.id] || ''}
                onChange={(v) =>
                  setAnswers((p) => ({ ...p, [currentQuestion.id]: v }))
                }
              />
            </div>

            {/* Navigation */}
            <div className="mt-6 flex items-center justify-between gap-3 border-t border-border/40 pt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrev}
                className="gap-1.5"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('navigation.previous')}
              </Button>

              {isLastQuestion ? (
                <Button
                  onClick={handleSubmit}
                  disabled={respondMutation.isPending}
                  className="gap-2 bg-linear-to-r from-primary to-xtreed-green-light text-primary-foreground shadow-lg hover:opacity-95 hover:shadow-xl hover:shadow-primary/30"
                >
                  {respondMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {t('navigation.submit')}
                </Button>
              ) : (
                <Button onClick={handleNext} className="group gap-1.5">
                  {t('navigation.next')}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* DONE */}
        {phase === 'done' && (
          <div className="space-y-5 p-8 text-center">
            <div className="relative mx-auto h-24 w-24">
              <div className="absolute inset-0 animate-ping rounded-full bg-success/20" />
              <div className="relative flex h-full w-full items-center justify-center rounded-full bg-linear-to-br from-success/20 to-success/5 ring-2 ring-success/30">
                <PartyPopper className="h-12 w-12 text-success-ink" />
              </div>
            </div>
            <div className="space-y-1">
              <h2 className="text-2xl font-bold">
                {t('done.title')}{' '}
                <Sparkles className="inline h-6 w-6 text-warning" />
              </h2>
              <p className="text-muted-foreground">{t('done.description')}</p>
            </div>
            {xpReward > 0 && (
              <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-warning/40 bg-linear-to-r from-warning/15 to-warning/15 px-4 py-2 text-sm font-bold text-warning-ink">
                <Zap className="h-4 w-4 fill-warning" />
                {t('done.xpEarned', { xp: xpReward })}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function QuestionInput({
  question,
  value,
  onChange,
}: {
  question: SurveyQuestion;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation('survey');
  const cfg = (question.config || {}) as Record<string, any>;
  const type = question.type;

  // Text / Number / Currency
  if (['text', 'number', 'currency'].includes(type)) {
    return (
      <Input
        type={type === 'text' ? 'text' : 'number'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.placeholder || ''}
        className="h-12 text-base"
        autoFocus
      />
    );
  }

  // Textarea
  if (type === 'textarea') {
    return (
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.placeholder || ''}
        rows={4}
        className="text-base"
        autoFocus
      />
    );
  }

  // Select
  if (type === 'select') {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-12 text-base">
          <SelectValue
            placeholder={question.placeholder || t('input.selectPlaceholder')}
          />
        </SelectTrigger>
        <SelectContent>
          {(cfg.options || []).map((opt: string) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // NPS / Scale / Rating
  if (['nps', 'scale', 'rating'].includes(type)) {
    const min = cfg.min ?? 0;
    const max = cfg.max ?? (type === 'nps' ? 10 : 5);
    const nums = Array.from({ length: max - min + 1 }, (_, i) => i + min);
    const isNps = type === 'nps';

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap justify-center gap-2">
          {nums.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(String(n))}
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-xl border-2 text-base font-bold transition-all',
                value === String(n)
                  ? 'scale-110 border-primary bg-primary text-primary-foreground shadow-lg'
                  : 'border-border hover:border-primary/50 hover:bg-secondary/40',
              )}
            >
              {n}
            </button>
          ))}
        </div>
        {isNps && (
          <div className="flex justify-between px-1 text-[10px]">
            <span className="text-destructive-ink">{t('input.npsLow')}</span>
            <span className="text-success-ink">{t('input.npsHigh')}</span>
          </div>
        )}
      </div>
    );
  }

  // Checkbox / Multiselect
  if (['checkbox', 'multiselect'].includes(type)) {
    const selected = value.split(',').filter(Boolean);
    return (
      <div className="space-y-2">
        {(cfg.options || []).map((opt: string) => {
          const isSelected = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => {
                const next = isSelected
                  ? selected.filter((s) => s !== opt)
                  : [...selected, opt];
                onChange(next.join(','));
              }}
              className={cn(
                'flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition-all',
                isSelected
                  ? 'border-primary bg-primary/10 text-primary-ink'
                  : 'border-border hover:border-primary/50',
              )}
            >
              <div
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-all',
                  isSelected ? 'border-primary bg-primary' : 'border-border',
                )}
              >
                {isSelected && (
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary-foreground" />
                )}
              </div>
              {opt}
            </button>
          );
        })}
      </div>
    );
  }

  // Fallback
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={question.placeholder || ''}
      className="h-12 text-base"
      autoFocus
    />
  );
}
